import { v4 as uuidv4 } from "uuid";
import { useGlobalStore } from "../../globalStore";
import { postToRenderer } from "../../message/utils";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ── 폴링/다운로드 한계값 ──
const POLL_INTERVAL_MS = 1000;
/** /status 한 번의 응답 대기 상한. 이 시간 안에 응답이 없으면 실패로 세고 다시 폴링한다. */
const STATUS_FETCH_TIMEOUT_MS = 15_000;
/** 서버 MAX_JOB_PROCESSING_SECONDS(200s)보다 넉넉하게. 이 시간을 넘기면 무한 대기 대신 에러로 끝낸다. */
const MAX_POLL_MS = 5 * 60 * 1000;
/** 404·5xx·알 수 없는 상태 등 비정상 폴링 응답이 이만큼 연속되면 포기한다. */
const MAX_CONSECUTIVE_POLL_FAILURES = 10;
/** /result 한 번의 다운로드 대기 상한. 수 MB 본문이 전송 중 멈춰도 여기서 끊고 재요청한다. */
const RESULT_FETCH_TIMEOUT_MS = 30_000;
/** /result 재시도 횟수. 서버 RESULT_CLAIM_GRACE(120s) 안에 끝나도록 (30s × 3회 + 백오프 ≈ 96s). */
const RESULT_FETCH_RETRIES = 2;

/** 재시도해도 소용없는 에러 (job 없음 등). 폴링 루프를 즉시 끝낸다. */
class NonRetryableError extends Error {}

/**
 * UXP fetch에는 타임아웃이 없어 연결이 멈추면 영원히 pending 상태가 된다.
 * Promise.race로 시간 상한을 둔다. (AbortController는 UXP 버전에 따라 지원이 달라 사용하지 않음)
 */
function fetchWithTimeout(
	url: string,
	timeoutMs: number,
	init?: RequestInit,
): Promise<Response> {
	return Promise.race([
		fetch(url, init),
		new Promise<Response>((_, reject) =>
			setTimeout(
				() =>
					reject(new Error(`Request timed out after ${timeoutMs}ms: ${url}`)),
				timeoutMs,
			),
		),
	]);
}

/**
 * 결과 JSON은 base64 이미지를 담아 수 MB가 되므로 전송 중 본문이 잘리는 일이 간헐적으로 있다.
 * (증상: "SyntaxError: Unterminated string in JSON at position N", N = 잘린 지점)
 * 본문을 text로 받아 Content-Length와 대조하고, 잘렸거나 파싱에 실패하면 같은 URL을 다시 요청한다.
 * 결과는 서버에 남아 있으므로 재생성 없이 복구된다.
 */
async function fetchJsonWithRetry(
	url: string,
	retries = RESULT_FETCH_RETRIES,
): Promise<any> {
	let lastError: unknown;
	for (let attempt = 0; attempt <= retries; attempt++) {
		try {
			const res = await fetchWithTimeout(url, RESULT_FETCH_TIMEOUT_MS);
			// 본문 읽기도 같은 시간 상한 안에 끝나야 한다 (헤더만 오고 본문이 멈추는 경우 대비)
			const text = await Promise.race([
				res.text(),
				new Promise<string>((_, reject) =>
					setTimeout(
						() => reject(new Error(`Response body stalled: ${url}`)),
						RESULT_FETCH_TIMEOUT_MS,
					),
				),
			]);
			const expected = Number(res.headers.get("content-length"));
			// text.length는 문자 수, Content-Length는 바이트 수. 문자 수 <= 바이트 수이므로
			// 문자 수가 더 작으면 확실히 잘린 것이다. (base64 본문은 ASCII라 사실상 동일)
			if (expected > 0 && text.length < expected) {
				throw new Error(
					`Truncated response body: received ${text.length} of ${expected} bytes`,
				);
			}
			return JSON.parse(text);
		} catch (err) {
			lastError = err;
			console.warn(
				`[fetchInferenceJob] result fetch failed (attempt ${attempt + 1}/${retries + 1}):`,
				err,
			);
			if (attempt < retries) await sleep(1000 * (attempt + 1));
		}
	}
	throw lastError;
}

export type FetchInferenceJobOptions = {
	params: Record<string, unknown>;
	executionContext: any;
	messageId: string;
	/** Custom API endpoint path (default: "/jobs") */
	apiPath?: string;
	/** Custom status endpoint builder (default: `${apiUrl}/${jobId}`) */
	statusEndpoint?: (apiUrl: string, jobId: string) => string;
	/** Custom result endpoint builder (default: `${apiUrl}/${jobId}/result`) */
	resultEndpoint?: (apiUrl: string, jobId: string) => string;
	/** Custom cancel endpoint builder (default: DELETE `${apiUrl}/${jobId}`) */
	cancelEndpoint?: (
		apiUrl: string,
		jobId: string,
	) => { url: string; method: string };
	/** Extract result from response data (default: `data.base64_list[0]`) */
	extractResult?: (data: any) => string;
	/** If true, use gatewayUrl instead of apiUrl (default: false) */
	useGatewayUrl?: boolean;
};

/**
 * Generic fetch function for inference jobs with polling and cancellation support.
 * Consolidates the common pattern used across all tool files.
 */
export async function fetchInferenceJob(
	options: FetchInferenceJobOptions,
): Promise<string | false> {
	const {
		params,
		executionContext,
		messageId,
		apiPath = "/jobs",
		statusEndpoint = (apiUrl, jobId) => `${apiUrl}/${jobId}`,
		resultEndpoint = (apiUrl, jobId) => `${apiUrl}/${jobId}/result`,
		cancelEndpoint = (apiUrl, jobId) => ({
			url: `${apiUrl}/${jobId}`,
			method: "DELETE",
		}),
		extractResult = (data) => data.base64_list[0],
		useGatewayUrl = false,
	} = options;

	const genericError = "Image generation failed. Please try again later.";

	const baseUrl = useGatewayUrl
		? useGlobalStore.getState().gatewayUrl
		: useGlobalStore.getState().getApiUrl();
	const apiUrl = `${baseUrl}${apiPath}`;

	// Build headers – include Authorization if jwt is present in params
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};
	const jwt = (params as Record<string, unknown>).jwt;
	if (typeof jwt === "string" && jwt) {
		headers.Authorization = `Bearer ${jwt}`;
	}

	// Create job
	const bodyParams = useGatewayUrl
		? { ...params, host_app: "photoshop" }
		: params;
	const res = await fetch(apiUrl, {
		method: "POST",
		headers,
		body: JSON.stringify(bodyParams),
	});

	// 게이트웨이가 항상 안전·브랜드비노출 메시지를 보내므로 서버 메시지를 그대로 노출한다.
	if (!res.ok) {
		const contentType = res.headers.get("content-type");
		if (contentType?.includes("application/json")) {
			const errorData = await res.json();
			throw new Error(errorData.detail || genericError);
		}
		throw new Error(genericError);
	}

	const data = await res.json();
	if (!data.success) {
		throw new Error(data.error || genericError);
	}

	const jobId = data.job_id as string;
	let cancelled = false;

	// Setup cancellation handler
	executionContext.onCancel = async () => {
		cancelled = true;
		const cancel = cancelEndpoint(apiUrl, jobId);
		await fetch(cancel.url, { method: cancel.method });
		postToRenderer([
			{
				key: "finish",
				id: uuidv4(),
				values: {
					id: messageId,
					status: "error",
				},
			},
		]);
	};

	// Poll for completion.
	// 무한 대기를 막기 위해 (1) 전체 폴링 시간 상한, (2) 요청당 타임아웃,
	// (3) 비정상 응답(404·5xx·비JSON·알 수 없는 상태) 연속 횟수 상한을 둔다.
	const pollStartedAt = Date.now();
	let consecutiveFailures = 0;
	let lastFailure: unknown;
	while (true) {
		if (cancelled) return false;
		if (Date.now() - pollStartedAt > MAX_POLL_MS) {
			throw new Error("Image generation timed out. Please try again.");
		}

		let statusData: any;
		let jobState: unknown;
		try {
			const statusRes = await fetchWithTimeout(
				statusEndpoint(apiUrl, jobId),
				STATUS_FETCH_TIMEOUT_MS,
			);
			if (statusRes.status === 404) {
				// Redis 상태 키가 사라짐 (서버 재시작·TTL 만료). 기다려도 돌아오지 않는다.
				throw new NonRetryableError(
					"The job was lost on the server. Please try again.",
				);
			}
			const contentType = statusRes.headers.get("content-type");
			if (!contentType?.includes("application/json")) {
				// 배포 중 프록시의 HTML 에러 페이지 등. 일시적일 수 있으므로 실패로 세고 재시도.
				const text = await statusRes.text();
				throw new Error(text || genericError);
			}
			statusData = await statusRes.json();
			if (!statusRes.ok) {
				throw new Error(
					statusData?.error || statusData?.detail || genericError,
				);
			}
			jobState = statusData.state ?? statusData.status;
		} catch (err) {
			if (err instanceof NonRetryableError) throw err;
			lastFailure = err;
			consecutiveFailures += 1;
			console.warn(
				`[fetchInferenceJob] status poll failed (${consecutiveFailures}/${MAX_CONSECUTIVE_POLL_FAILURES}):`,
				err,
			);
			if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
				throw new Error(
					lastFailure instanceof Error && lastFailure.message
						? lastFailure.message
						: genericError,
				);
			}
			await sleep(POLL_INTERVAL_MS * Math.min(consecutiveFailures, 5));
			continue;
		}

		if (jobState === "completed" || jobState === "done") break;
		if (jobState === "error") {
			throw new Error(statusData.error || genericError);
		}
		if (jobState === "cancelled") {
			// 서버 측에서 취소됨 (클라이언트가 취소하지 않았어도). 계속 폴링하면 영원히 돈다.
			return false;
		}
		// 그 외(processing 등 진행 중 상태, API별로 다른 이름 포함)는 계속 폴링한다.
		// 무한 대기는 위의 MAX_POLL_MS 상한이 막는다.
		consecutiveFailures = 0;
		await sleep(POLL_INTERVAL_MS);
	}

	if (cancelled) return false;

	// Get result (본문이 잘리거나 전송이 멈추면 재요청. 서버는 RESULT_CLAIM_GRACE 동안 결과를 보관한다)
	let resultData: any;
	try {
		resultData = await fetchJsonWithRetry(resultEndpoint(apiUrl, jobId));
	} catch (err) {
		console.error(err);
		throw new Error(
			"Failed to download the generated image. Please check your network connection and try again.",
		);
	}

	if (cancelled) return false;
	if (!resultData.success) {
		throw new Error(resultData.error || genericError);
	}

	return extractResult(resultData);
}

/**
 * Simplified version for standard /jobs endpoint tools.
 * Uses: DELETE cancel, /jobs endpoint, base64_list[0] result
 */
export async function fetchStandardInferenceJob<
	T extends Record<string, unknown>,
>(
	params: T,
	executionContext: any,
	messageId: string,
): Promise<string | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
	});
}

/**
 * Simplified version for gateway /infer/oplux-tools endpoint tools.
 * Uses: POST cancel, gateway URL, image_base64 result
 */
export async function fetchGatewayInferenceJob<
	T extends Record<string, unknown>,
>(
	params: T,
	executionContext: any,
	messageId: string,
): Promise<string | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath: "/infer/oplux-tools",
		statusEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/oplux-tools", "")}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/oplux-tools", "")}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl.replace("/infer/oplux-tools", "")}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult: (data) => data.image_base64,
		useGatewayUrl: true,
	});
}

/**
 * Helper for APIs using POST cancel with /status/{jobId}, /result/{jobId} pattern.
 * Used by: seedream, gemini, face-blend, image-upscale
 */
export async function fetchPostCancelInferenceJob<
	T extends Record<string, unknown>,
>(
	params: T,
	executionContext: any,
	messageId: string,
	apiPath: string,
	extractResult: (data: any) => string = (data) => data.base64,
): Promise<string | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath,
		statusEndpoint: (apiUrl, jobId) => `${apiUrl}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) => `${apiUrl}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult,
	});
}
